import { captureMessage } from '@sentry/react'
import * as turf from '@turf/turf'
import type { GuidanceFieldFeatures, RegionCollection } from 'api-validations/geojson-schemas'
import {
  boundaryCorridorPropertiesSchema,
  boundaryInspectApi,
  BoundaryInspectGroup
} from 'api-validations/helm/boundary-inspect'
import type { HeadlandCornerType } from 'api-validations/locutus/guidance'
import type { Feature, Geometry as GeoJsonGeometry, LineString, Point, Polygon } from 'geojson'
import { useCallback } from 'react'
import { keepPreviousData, usePanoramaQuery } from 'shared-ui/api-provider'

export type HelmRegion = {
  id: string
  region: string
  feature: Feature<Polygon>
}

export type HelmEdge = {
  id: string
  region: string
  ring: string
  feature: Feature<LineString>
}

// Helm-inspect returns holes as LineStrings — the interior boundary ring of
// each hole — but the Overrides display renders and hit-tests them as filled
// polygons (see the `holes` build below where the ring is wrapped in a
// Polygon). `id` is the id helm minted for this hole ring in this response;
// helm rejects any other id as `unknown override id`. `region` is the owning
// region's id and is used downstream to hide edges/corners on the boundary
// of a hole that's been marked passable.
export type HelmHole = {
  id: string
  region: string
  feature: Feature<Polygon>
}

export type HelmCorner = {
  id: string
  region: string
  ring: string
  convex: boolean
  interiorAngle: number
  edges: [string, string]
  feature: Feature<Point>
}

// A corridor helm detected between two regions: two facing edges separated by
// a strip that belongs to neither (a waterway, grassed strip or lane). The
// feature is the closed strip polygon between the banks. `id` is what a
// corridor override on the planning call targets; `regions` are the two
// boundary feature ids it joins and `edgesA`/`edgesB` the side ids each bank
// spans.
export type HelmCorridor = {
  id: string
  regions: [string, string]
  edgesA: string[]
  edgesB: string[]
  widthM: number
  lengthM: number
  feature: Feature<Polygon>
}

export type HelmInspect = {
  regions: GuidanceFieldFeatures
  edges: HelmEdge[]
  holes: HelmHole[]
  corners: HelmCorner[]
  corridors: HelmCorridor[]
}

export type HelmInspectRequest = { boundary: RegionCollection; cornerType: HeadlandCornerType }

const NO_REGIONS: RegionCollection = { type: 'FeatureCollection', features: [] }

/**
 * The polygons of a feature list as an inspect boundary, each identified by
 * the zone it belongs to.
 */
export const toInspectRegions = (features: Feature[]): RegionCollection => ({
  type: 'FeatureCollection',
  features: features.flatMap((feature) => {
    const { geometry } = feature
    if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return []
    const zone = (feature.properties as { zone?: string } | null)?.zone
    const region =
      geometry.type === 'MultiPolygon' && geometry.coordinates.length === 1
        ? { type: 'Polygon' as const, coordinates: geometry.coordinates[0] }
        : geometry
    return [{ type: 'Feature', id: zone, geometry: region, properties: feature.properties }]
  })
})

export const useHelmInspect = (request: HelmInspectRequest | null) => {
  const boundary = request?.boundary ?? NO_REGIONS
  const select = useCallback(
    (features: GuidanceFieldFeatures) => toHelmInspect({ features, boundary }),
    [boundary]
  )
  return usePanoramaQuery(
    boundaryInspectApi,
    undefined,
    {
      enabled: request !== null,
      placeholderData: keepPreviousData,
      staleTime: Number.POSITIVE_INFINITY,
      select
    },
    request ?? { boundary }
  )
}

export type HelmInspectQuery = ReturnType<typeof useHelmInspect>

export const toHelmInspect = ({
  features,
  boundary
}: {
  features: GuidanceFieldFeatures
  boundary: RegionCollection
}): HelmInspect => {
  // The wire schema types most of properties as an open record but
  // guarantees `id`. Narrow the fields the planning UI reads so downstream
  // code doesn't repeat casts on every access.
  type InspectProps = {
    id: string
    group?: string
    region?: string
    ring?: string
    exterior?: boolean
    convex?: boolean
    interiorAngle?: number
    edges?: string[]
  }

  const toFeature = <G extends GeoJsonGeometry>(props: InspectProps, geometry: G): Feature<G> => ({
    type: 'Feature' as const,
    id: props.id,
    geometry,
    properties: props
  })

  const helmRegions: HelmRegion[] = []
  const edges: HelmEdge[] = []
  const corners: HelmCorner[] = []
  const holes: HelmHole[] = []
  const corridors: HelmCorridor[] = []

  for (const f of features.features ?? []) {
    const p = f.properties as InspectProps
    if (
      p.group === BoundaryInspectGroup.BoundaryHoles &&
      f.geometry.type === 'LineString' &&
      p.region !== undefined &&
      p.region !== ''
    ) {
      // Wrap the ring LineString as a Polygon so the view-only layer
      // renders a filled, clickable region. Close the ring if helm
      // returned it open (first !== last). The OL feature id, the
      // `hole.id`, and the id sent to helm as a hole override all have to
      // be `p.id`: helm rejects any other id with `unknown override id`,
      // and the popup finds the hole by the picked feature id.
      const ring = f.geometry.coordinates as [number, number][]
      if (ring.length < 3) continue
      const first = ring[0]
      const last = ring[ring.length - 1]
      const closed = first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first]
      const polygon: Feature<Polygon> = {
        type: 'Feature',
        id: p.id,
        geometry: { type: 'Polygon', coordinates: [closed] },
        properties: p
      }
      holes.push({ id: p.id, region: p.region, feature: polygon })
    } else if (
      p.group === BoundaryInspectGroup.BoundarySides &&
      f.geometry.type === 'LineString' &&
      p.region !== undefined &&
      p.ring !== undefined
    ) {
      edges.push({
        id: p.id,
        region: p.region,
        ring: p.ring,
        feature: toFeature(p, f.geometry)
      })
    } else if (
      p.group === BoundaryInspectGroup.BoundaryCorners &&
      f.geometry.type === 'Point' &&
      p.region !== undefined &&
      p.ring !== undefined &&
      p.convex !== undefined &&
      p.interiorAngle !== undefined &&
      p.edges?.length === 2
    ) {
      corners.push({
        id: p.id,
        region: p.region,
        ring: p.ring,
        convex: p.convex,
        interiorAngle: p.interiorAngle,
        edges: [p.edges[0], p.edges[1]],
        feature: toFeature(p, f.geometry)
      })
    } else if (
      p.group === BoundaryInspectGroup.BoundaryCorridors &&
      f.geometry.type === 'Polygon'
    ) {
      const parsed = boundaryCorridorPropertiesSchema.safeParse(f.properties)
      if (!parsed.success) {
        // Surface a contract drift on helm's side rather than letting it
        // read as "no corridors detected".
        captureMessage('helm-inspect: dropping a corridor that failed its schema', {
          level: 'warning',
          extra: { corridorId: p.id, issues: parsed.error.issues }
        })
        continue
      }
      const c = parsed.data
      corridors.push({
        id: c.id,
        regions: c.regions,
        edgesA: c.edgesA,
        edgesB: c.edgesB,
        widthM: c.widthM,
        lengthM: c.lengthM,
        feature: {
          type: 'Feature',
          id: c.id,
          geometry: f.geometry,
          properties: c
        }
      })
    }
  }

  // Stamp each edge/corner with a point that's guaranteed to lie inside
  // its owning region's polygon so the view-only style can spread
  // coincident features (shared borders, shared corners between adjacent
  // regions) apart visually — each feature renders offset toward the
  // region it belongs to instead of stacking on the exact same
  // coordinates. `pointOnFeature` beats `centroid` for concave regions:
  // a C-shaped zone's centroid lands in the void outside the polygon,
  // which would push the offset the wrong direction on edges facing the
  // interior. This "interior anchor" is always inside the shape, so the
  // sign test for edges always picks the perpendicular pointing into the
  // region.
  // Build the region-interior map from the polygons we *sent* to helm,
  // keyed on zone id. Helm's boundary-inspect
  // response doesn't consistently echo those polygons back under a known
  // group tag, so parsing them from the response leaves the map empty
  // and every corner/edge would be dropped with `regionCentroid: undefined`.
  const regionInteriors = new Map<string, [number, number]>()
  for (const region of boundary.features) {
    const zone = region.id
    if (typeof zone !== 'string' || zone === '') continue
    const anchor = turf.pointOnFeature(region).geometry.coordinates as [number, number]
    regionInteriors.set(zone, anchor)
  }
  const stampInterior = <G extends GeoJsonGeometry>(
    feat: Feature<G>,
    region: string
  ): Feature<G> => {
    const anchor = regionInteriors.get(region)
    if (anchor === undefined) return feat
    return {
      ...feat,
      properties: { ...feat.properties, regionCentroid: anchor }
    }
  }
  for (const edge of edges) {
    edge.feature = stampInterior(edge.feature, edge.region)
  }
  for (const corner of corners) {
    corner.feature = stampInterior(corner.feature, corner.region)
  }

  return {
    regions: features,
    edges,
    holes,
    corners,
    corridors
  }
}
